import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Chip,
  Button,
  CircularProgress
} from '@mui/material';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import {
  TrendingUp,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  Eye,
  EyeOff,
  Filter
} from 'lucide-react';
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer
} from 'recharts';

import cashFlowService from '../services/AGTUps-product-services/cashFlowService';

/** 
 * Paleta de colores / theme 
 * similar a los otros shortcuts
 */
const COLORS = {
  primary: 'rgba(128, 128, 128, 0.5)',
  text: {
    light: '#FFFFFF',
    dark: '#FFFFFF'
  }
};

const createCustomTheme = (mode, actualMode) =>
  createTheme({
    palette: {
      mode,
      actualMode
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none'
            }
          },
          contained: {
            backgroundColor: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.primary,
            color: actualMode === 'wallpaper' ? '#000000' : '#ffffff',
            '&:hover': {
              backgroundColor: actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.85)' : COLORS.primary
            },
            '&:disabled': {
              backgroundColor:
                mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.12)'
                  : 'rgba(0, 0, 0, 0.12)'
            }
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: '16px',
            border: '1px solid',
            borderColor: actualMode === 'wallpaper'
              ? 'rgba(128, 128, 128, 0.3)'
              : (mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)'),
            backgroundColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : undefined,
            backdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
            WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none'
          }
        }
      }
    }
  });

// ======== Componente Shortcut: CurrentFlowShortcut ========
function CurrentFlowShortcut({ open, onClose }) {
  // En lugar de forzar detección por matchMedia,
  // tomamos el modo del theme "padre"
  const parentTheme = useTheme();
  const customTheme = createCustomTheme(parentTheme.palette.mode, parentTheme.palette.actualMode);

  const [timeRange, setTimeRange] = useState('6m');
  const [showBalance, setShowBalance] = useState(true);
  const [cashFlowData, setCashFlowData] = useState([]);
  const [dailyFlowData, setDailyFlowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dailyFilter, setDailyFilter] = useState('today');
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [financialInsights, setFinancialInsights] = useState(null);

  // ============= EQUIVALENTE A TU 'BalanceOverview' =============
  useEffect(() => {
    if (open) {
      // Cada vez que se abra el shortcut, recargamos la data
      fetchCashFlow();
      fetchLatestInsight();
    } else {
      // Limpiar estados si se desea
      resetStates();
    }
    // eslint-disable-next-line
  }, [open]);

  const fetchCashFlow = async () => {
    setLoading(true);
    try {
      const monthlyResult = await cashFlowService.getCashFlow({
        range: timeRange,
        aggregation: 'monthly'
      });
      const dailyResult = await cashFlowService.getDailyCashFlow({
        range: timeRange
      });
      if (monthlyResult.success && dailyResult.success) {
        setCashFlowData(monthlyResult.data);
        setDailyFlowData(dailyResult.data);
      }
    } catch (error) {
      console.error('Error fetching cash flow:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestInsight = async () => {
    try {
      const insightResult = await cashFlowService.getLatestInsight();
      if (insightResult.success && insightResult.data) {
        setFinancialInsights(insightResult.data);
      }
    } catch (error) {
      console.error('Error fetching latest insight:', error);
    }
  };

  const resetStates = () => {
    // Opcional: restaurar valores por defecto
    setTimeRange('6m');
    setShowBalance(true);
    setFinancialInsights(null);
  };

  // Cuando el usuario cambie `timeRange`
  useEffect(() => {
    if (open) {
      // Si el dialog está abierto, recargamos datos
      fetchCashFlow();
    }
    // eslint-disable-next-line
  }, [timeRange]);

  // Handler para generar insights
  const handleGenerateInsights = async () => {
    setInsightsLoading(true);
    try {
      const response = await cashFlowService.generateClaudeInsights();
      if (response.success) {
        setFinancialInsights(response.data);
      } else {
        console.error('Error generating insights:', response.error);
      }
    } catch (error) {
      console.error('Error generating insights:', error);
    } finally {
      setInsightsLoading(false);
    }
  };

  // Cálculos de balance actual
  const currentBalance = cashFlowData.reduce(
    (acc, record) => acc + (record.income || 0) - (record.expenses || 0),
    0
  );

  // Multiplicador
  const count = cashFlowData.length;
  let multiplier = 1;
  if (count >= 200) multiplier = 1.2;
  else if (count >= 100) multiplier = 1.15;
  else if (count >= 50) multiplier = 1.1;
  else if (count >= 20) multiplier = 1.05;

  const projectedBalance = currentBalance * multiplier;

  // Inyectar currentBalance en cada registro para el chart
  const chartData = cashFlowData.map((rec) => ({
    ...rec,
    currentBalance: currentBalance
  }));

  // Lógica de dailyFilter
  const getFilteredDailyData = () => {
    const today = new Date();
    const getDateStr = (date) => date.toISOString().split('T')[0];

    let fromStr, toStr;
    switch (dailyFilter) {
      case 'today': {
        const todayStr = getDateStr(today);
        fromStr = todayStr;
        toStr = todayStr;
        break;
      }
      case '1 week': {
        const fromDate = new Date();
        fromDate.setDate(today.getDate() - 7);
        fromStr = getDateStr(fromDate);
        toStr = getDateStr(today);
        break;
      }
      case '2 weeks': {
        const fromDate = new Date();
        fromDate.setDate(today.getDate() - 14);
        fromStr = getDateStr(fromDate);
        toStr = getDateStr(today);
        break;
      }
      case '3 weeks': {
        const fromDate = new Date();
        fromDate.setDate(today.getDate() - 21);
        fromStr = getDateStr(fromDate);
        toStr = getDateStr(today);
        break;
      }
      case '4 weeks': {
        const fromDate = new Date();
        fromDate.setDate(today.getDate() - 28);
        fromStr = getDateStr(fromDate);
        toStr = getDateStr(today);
        break;
      }
      case 'monthly': {
        const fromDate = new Date();
        fromDate.setDate(today.getDate() - 30);
        fromStr = getDateStr(fromDate);
        toStr = getDateStr(today);
        break;
      }
      default: {
        const fromDate = new Date();
        fromDate.setDate(today.getDate() - 7);
        fromStr = getDateStr(fromDate);
        toStr = getDateStr(today);
      }
    }
    return dailyFlowData.filter((d) => d.period >= fromStr && d.period <= toStr);
  };

  // Quick Stats: daily average, growth rate, etc.
  const filteredDaily = getFilteredDailyData();
  const totalDailyBalance = filteredDaily.reduce(
    (acc, day) => acc + (day.balance || 0),
    0
  );
  const avgDailyBalance =
    filteredDaily.length > 0 ? totalDailyBalance / filteredDaily.length : 0;
  let growthPercentage = 0;
  if (cashFlowData.length >= 2) {
    const last = cashFlowData[cashFlowData.length - 1];
    const prev = cashFlowData[cashFlowData.length - 2];
    if ((prev.balance || 0) !== 0) {
      growthPercentage = ((last.balance - prev.balance) / prev.balance) * 100;
    }
  }

  const renderMainContent = () => {
    if (loading) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    // Tomamos el modo actual para fondos etc.
    const isDark = parentTheme.palette.mode === 'dark';

    return (
      <Grid container spacing={3}>
        {/* SECCIÓN PRINCIPAL DEL GRÁFICO */}
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              backgroundColor: isDark ? '#272729' : '#F2F2F7',
              borderRadius: 2,
              p: 2
            }}
          >
            {/* ENCABEZADO */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: 2
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Cash Flow Overview
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Chip
                  size="small"
                  icon={<ArrowUpRight size={14} />}
                  label="Income"
                  color="success"
                  sx={{ fontSize: '0.75rem' }}
                />
                <Chip
                  size="small"
                  icon={<ArrowDownRight size={14} />}
                  label="Expenses"
                  color="error"
                  sx={{ fontSize: '0.75rem' }}
                />
                <Chip
                  size="small"
                  icon={<DollarSign size={14} />}
                  label="Balance"
                  color="primary"
                  sx={{ fontSize: '0.75rem' }}
                />
              </Box>
            </Box>
            {/* CHART */}
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="income" fill="rgba(128, 128, 128, 0.6)" />
                  <Bar dataKey="expenses" fill="rgba(128, 128, 128, 0.6)" />
                  <Bar dataKey="currentBalance" fill="rgba(128, 128, 128, 0.6)" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Grid>

        {/* PANEL DERECHO */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3
            }}
          >
            {/* CURRENT BALANCE */}
            <Box
              sx={{
                backgroundColor: isDark ? '#272729' : '#F2F2F7',
                borderRadius: 2,
                p: 2
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Current Balance
              </Typography>
              <Typography variant="h4">
                {showBalance ? `$${currentBalance.toLocaleString()}` : '••••••'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                <TrendingUp size={16} color="rgba(128, 128, 128, 0.6)" />
                <Typography variant="body2" color="success.main">
                  +15% vs last month
                </Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Projected Balance
                </Typography>
                <Typography variant="h5" color="primary">
                  {showBalance
                    ? `$${projectedBalance.toLocaleString()}`
                    : '••••••'}
                </Typography>
              </Box>
            </Box>

            {/* DAILY CASH FLOW */}
            <Box
              sx={{
                backgroundColor: isDark ? '#272729' : '#F2F2F7',
                borderRadius: 2,
                p: 2
              }}
            >
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Daily Cash Flow
                </Typography>
                <FormControl size="small">
                  <Select
                    value={dailyFilter}
                    onChange={(e) => setDailyFilter(e.target.value)}
                    sx={{ borderRadius: 1 }}
                  >
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="1 week">1 Week</MenuItem>
                    <MenuItem value="2 weeks">2 Weeks</MenuItem>
                    <MenuItem value="3 weeks">3 Weeks</MenuItem>
                    <MenuItem value="4 weeks">4 Weeks</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={getFilteredDailyData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="income" fill="rgba(128, 128, 128, 0.6)" />
                    <Bar dataKey="expenses" fill="rgba(128, 128, 128, 0.6)" />
                    <Bar dataKey="balance" fill="rgba(128, 128, 128, 0.6)" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>

            {/* QUICK STATS */}
            <Box
              sx={{
                backgroundColor: isDark ? '#272729' : '#F2F2F7',
                borderRadius: 2,
                p: 2
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mb: 2 }}
              >
                Quick Stats
              </Typography>
              <Grid container spacing={2}>
                {/* Net Balance */}
                <Grid item xs={4}>
                  <Typography variant="body2">Net Balance</Typography>
                  <Typography variant="h6">
                    {showBalance
                      ? `$${currentBalance.toLocaleString()}`
                      : '••••••'}
                  </Typography>
                  <Typography variant="caption" color="primary">
                    Income - Expenses
                  </Typography>
                </Grid>
                {/* Daily Average */}
                <Grid item xs={4}>
                  <Typography variant="body2">Daily Average</Typography>
                  <Typography variant="h6">
                    {showBalance
                      ? `$${avgDailyBalance.toLocaleString(undefined, {
                          maximumFractionDigits: 2
                        })}`
                      : '••••••'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Average daily balance
                  </Typography>
                </Grid>
                {/* Growth Rate */}
                <Grid item xs={4}>
                  <Typography variant="body2">Growth Rate</Typography>
                  <Typography
                    variant="h6"
                    color={growthPercentage >= 0 ? 'success.main' : 'error.main'}
                  >
                    {showBalance ? `${growthPercentage.toFixed(2)}%` : '••••••'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    vs previous period
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Grid>

        {/* FINANCIAL INSIGHTS */}
        <Grid item xs={12}>
          <Box
            sx={{
              backgroundColor: isDark ? '#272729' : '#F2F2F7',
              borderRadius: 2,
              p: 2
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Financial Insights
              </Typography>
              <Button
                variant="contained"
                onClick={handleGenerateInsights}
                disabled={insightsLoading}
                sx={{ textTransform: 'none', borderRadius: '20px' }}
              >
                {insightsLoading ? <CircularProgress size={20} /> : 'Generate Insights'}
              </Button>
            </Box>
            {financialInsights && financialInsights.insights ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Liquidity
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: isDark ? 'rgba(128, 128, 128, 0.25)' : 'rgba(128, 128, 128, 0.15)',
                    borderRadius: 1
                  }}
                >
                  <Typography variant="body2">
                    Current Liquidity: {financialInsights.insights.liquidity.currentLiquidity}
                  </Typography>
                  <Typography variant="body2">
                    Recommendations:{' '}
                    {financialInsights.insights.liquidity.recommendations.join(', ')}
                  </Typography>
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Expense Control
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: isDark ? 'rgba(128, 128, 128, 0.25)' : 'rgba(128, 128, 128, 0.15)',
                    borderRadius: 1
                  }}
                >
                  <Typography variant="body2">
                    Suggestions:{' '}
                    {financialInsights.insights.expenseControl.suggestions.join(', ')}
                  </Typography>
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Financing Recommendations
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: isDark ? 'rgba(128, 128, 128, 0.25)' : 'rgba(128, 128, 128, 0.15)',
                    borderRadius: 1
                  }}
                >
                  <Typography variant="body2">
                    Options:{' '}
                    {financialInsights.insights.financingRecommendations.options.join(
                      ', '
                    )}
                  </Typography>
                  <Typography variant="body2">
                    Notes: {financialInsights.insights.financingRecommendations.notes}
                  </Typography>
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Summary
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: isDark ? 'rgba(128, 128, 128, 0.25)' : 'rgba(128, 128, 128, 0.15)',
                    borderRadius: 1
                  }}
                >
                  <Typography variant="body2">
                    {financialInsights.insights.summary}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No insights generated yet. Click the button above to generate liquidity
                insights.
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    );
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        aria-labelledby="current-flow-dialog"
      >
        {/* Title + Actions */}
        <DialogTitle
          id="current-flow-dialog"
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Current Flow
          </Typography>
          <Box>
            {/* Botones del panel superior (Filter, Download, Show/Hide balance) */}
            <Tooltip title="Filter">
              <IconButton>
                <Filter size={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download Report">
              <IconButton>
                <Download size={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title={showBalance ? 'Hide Balance' : 'Show Balance'}>
              <IconButton onClick={() => setShowBalance(!showBalance)}>
                {showBalance ? <EyeOff size={20} /> : <Eye size={20} />}
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>

        {/* Contenido Principal */}
        <DialogContent dividers sx={{ minHeight: '500px' }}>
          <Box sx={{ mb: 2 }}>
            {/* Filtro de Time Range */}
            <FormControl variant="outlined" size="small">
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={(e) => setTimeRange(e.target.value)}
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="1m">Last Month</MenuItem>
                <MenuItem value="3m">Last 3 Months</MenuItem>
                <MenuItem value="6m">Last 6 Months</MenuItem>
                <MenuItem value="1y">Last Year</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {renderMainContent()}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              color: parentTheme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : undefined,
              borderColor: parentTheme.palette.actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.3)' : undefined
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}

export default CurrentFlowShortcut;
